import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import {
  Search,
  AccessTime,
  Download,
  KeyboardArrowDown,
  PictureAsPdf,
  VpnKey,
  BarChart as BarChartIcon,
  Replay,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api, { getPastPaperResultPath } from '../../../api';

const ParentPastPaperResults = () => {
  const navigate = useNavigate();
  const isTester = (localStorage.getItem('role') || '').toLowerCase() === 'tester';
  const [attempts, setAttempts] = useState([]);
  const [downloadingPdfId, setDownloadingPdfId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [search, setSearch] = useState('');
  const [searchSubmitted, setSearchSubmitted] = useState('');
  const [allowAnswerKeyDownload, setAllowAnswerKeyDownload] = useState(true);
  const [downloadingPaperId, setDownloadingPaperId] = useState(null);
  const [downloadMenu, setDownloadMenu] = useState({ anchor: null, row: null });
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 25,
    total: 0,
  });

  useEffect(() => {
    if (isTester) {
      setAllowAnswerKeyDownload(true);
      return;
    }
    api.get('parent/settings')
      .then((res) => setAllowAnswerKeyDownload(res.data?.data?.allow_answer_key_download !== false))
      .catch(() => setAllowAnswerKeyDownload(true));
  }, [isTester]);

  const fetchAttempts = useCallback(async (page = 1, studentId = '', searchTerm = '') => {
    setLoading(true);
    try {
      const params = { page, per_page: 25 };
      if (studentId) params.student_id = studentId;
      if (searchTerm) params.search = searchTerm;
      const res = await api.get('parent/paper/results', { params });
      const payload = res.data?.data ?? {};
      const list = Array.isArray(payload?.data) ? payload.data : [];
      setAttempts(list);
      if (Array.isArray(payload?.students) && payload.students.length > 0) {
        setStudents(payload.students);
      }
      setPagination({
        current_page: payload?.current_page ?? 1,
        last_page: payload?.last_page ?? 1,
        per_page: payload?.per_page ?? 25,
        total: payload?.total ?? 0,
      });
    } catch {
      setAttempts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAttempts(pagination.current_page, selectedStudentId, searchSubmitted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current_page, selectedStudentId, searchSubmitted, fetchAttempts]);

  const handleSearch = (e) => {
    e?.preventDefault();
    setSearchSubmitted(search);
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handleClear = () => {
    setSearch('');
    setSearchSubmitted('');
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handleDownloadAnswerKey = async (row) => {
    if (!allowAnswerKeyDownload) {
      toast.error('Answer key downloads are disabled by your administrator.');
      return;
    }
    setDownloadingPaperId(row.paper_id);
    try {
      const response = await api.get(`parent/paper/${row.paper_id}/download-answer-key`, { responseType: 'blob' });
      const contentType = (response.headers?.['content-type'] || '').toLowerCase();
      if (!contentType.includes('application/pdf')) {
        let message = 'Failed to download answer key.';
        try {
          const text = await response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.message) message = parsed.message;
        } catch (_) {}
        toast.error(message);
        return;
      }
      const safeName = (row.paper_name || `paper-${row.paper_id}`)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${safeName || `paper-${row.paper_id}`}-answer-key.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Answer key download started');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to download answer key');
    } finally {
      setDownloadingPaperId(null);
    }
  };

  const handleDownloadPaperPdf = async (row) => {
    setDownloadingPdfId(row.paper_id);
    try {
      const response = await api.get(`parent/paper/${row.paper_id}/download-pdf`, { responseType: 'blob' });
      const contentType = (response.headers?.['content-type'] || '').toLowerCase();
      if (!contentType.includes('application/pdf')) {
        let message = 'Failed to download paper PDF.';
        try {
          const text = await response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.message) message = parsed.message;
        } catch (_) {}
        toast.error(message);
        return;
      }
      const safeName = (row.paper_name || `paper-${row.paper_id}`)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${safeName || `paper-${row.paper_id}`}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Paper PDF download started');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to download paper PDF');
    } finally {
      setDownloadingPdfId(null);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return 'N/A';
    try {
      return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return iso;
    }
  };

  const formatPct = (v) => {
    const n = Number(v);
    if (Number.isNaN(n)) return '—';
    return Math.round(n * 10) / 10;
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
          Practice Paper Results
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View completed practice paper attempts for your students.
        </Typography>
      </Box>

      <Card sx={{ borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3, alignItems: 'center' }}>
            {students.length > 1 && (
              <FormControl size="small" sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' }, minWidth: { sm: 200 } }}>
                <InputLabel>All Students</InputLabel>
                <Select
                  value={selectedStudentId}
                  label="All Students"
                  onChange={(e) => {
                    setSelectedStudentId(e.target.value);
                    setPagination((prev) => ({ ...prev, current_page: 1 }));
                  }}
                >
                  <MenuItem value="">All Students</MenuItem>
                  {students.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Box component="form" onSubmit={handleSearch} sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                placeholder="Search paper name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: { sm: 220 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button type="submit" variant="contained" size="medium">
                Search
              </Button>
            </Box>
            {searchSubmitted && (
              <Button variant="outlined" size="small" onClick={handleClear}>
                Clear
              </Button>
            )}
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={40} />
            </Box>
          ) : attempts.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              No completed practice paper attempts found
              {(selectedStudentId || searchSubmitted) ? ' matching your filters' : ' for your students yet'}.
            </Alert>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1, overflow: 'auto' }}>
                <Table size="medium">
                  <TableHead>
                    <TableRow>
                      {(students.length > 1 || isTester) && (
                        <TableCell sx={{ fontWeight: 700 }}>Student</TableCell>
                      )}
                      <TableCell sx={{ fontWeight: 700 }}>Paper</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Date Completed</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Time Taken</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Score</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Attempt #</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        View
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {attempts.map((row) => (
                      <TableRow key={row.id} hover>
                        {(students.length > 1 || isTester) && (
                          <TableCell sx={{ fontWeight: 500 }}>{row.student_name}</TableCell>
                        )}
                        <TableCell sx={{ fontWeight: 500 }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {row.paper_name}
                            </Typography>
                            {row.category_name && (
                              <Typography variant="caption" color="text.secondary">
                                {row.category_name}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{formatDate(row.completed_at)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
                            {row.time_taken_min != null
                              ? `${row.time_taken_min} min`
                              : row.time_taken_sec != null
                                ? `${Math.round(Number(row.time_taken_sec) / 60)} min`
                                : 'N/A'}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={row.total_marks != null ? `${Number(row.score ?? 0).toFixed(1)} / ${Number(row.total_marks).toFixed(1)} marks` : 'Score'}>
                            <Chip
                              size="small"
                              icon={<BarChartIcon sx={{ fontSize: 13 }} />}
                              label={`${formatPct(row.percentage)}%`}
                              color={Number(row.percentage) >= 50 ? 'success' : 'error'}
                              sx={{ fontWeight: 600 }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {row.attempt_number > 1 && (
                              <Replay sx={{ fontSize: 14, color: 'text.secondary' }} />
                            )}
                            <Typography variant="body2">#{row.attempt_number ?? 1}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => navigate(getPastPaperResultPath(row.purchase_id ?? row.id, 'result'))}
                            sx={{ mr: 0.5 }}
                          >
                            Report
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => navigate(getPastPaperResultPath(row.purchase_id ?? row.id, 'review'))}
                            sx={{ mr: 0.5 }}
                          >
                            Review
                          </Button>
                          {/* Every row is a paper the child has (bought, pack or plan); the API re-checks. */}
                          <Button
                            size="small"
                            variant="outlined"
                            endIcon={
                              (downloadingPdfId === row.paper_id || downloadingPaperId === row.paper_id)
                                ? <CircularProgress size={14} />
                                : <KeyboardArrowDown />
                            }
                            startIcon={<Download />}
                            disabled={downloadingPdfId === row.paper_id || downloadingPaperId === row.paper_id}
                            onClick={(e) => setDownloadMenu({ anchor: e.currentTarget, row })}
                          >
                            Download
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Menu
                anchorEl={downloadMenu.anchor}
                open={Boolean(downloadMenu.anchor)}
                onClose={() => setDownloadMenu({ anchor: null, row: null })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem
                  onClick={() => {
                    const row = downloadMenu.row;
                    setDownloadMenu({ anchor: null, row: null });
                    if (row) handleDownloadPaperPdf(row);
                  }}
                >
                  <ListItemIcon><PictureAsPdf fontSize="small" /></ListItemIcon>
                  <ListItemText>Paper PDF</ListItemText>
                </MenuItem>
                {allowAnswerKeyDownload && (
                  <MenuItem
                    onClick={() => {
                      const row = downloadMenu.row;
                      setDownloadMenu({ anchor: null, row: null });
                      if (row) handleDownloadAnswerKey(row);
                    }}
                  >
                    <ListItemIcon><VpnKey fontSize="small" /></ListItemIcon>
                    <ListItemText>Answer Key</ListItemText>
                  </MenuItem>
                )}
              </Menu>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {pagination.total} result{pagination.total !== 1 ? 's' : ''} total
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={pagination.current_page <= 1}
                    onClick={() => setPagination((prev) => ({ ...prev, current_page: Math.max(1, prev.current_page - 1) }))}
                  >
                    Previous
                  </Button>
                  <Typography variant="body2" sx={{ alignSelf: 'center', px: 1 }}>
                    {pagination.current_page} / {pagination.last_page}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={pagination.current_page >= pagination.last_page}
                    onClick={() => setPagination((prev) => ({ ...prev, current_page: Math.min(prev.last_page, prev.current_page + 1) }))}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ParentPastPaperResults;
